/**
 * shared.ts — Tipos e rótulos do domínio de Ordens de Serviço.
 */
export interface Loja {
  id: number;
  codigo: string;
  nome: string;
}

/**
 * A página de OS de uma loja: `/ordens-servico/loja/bz`.
 *
 * Caminho, e não `?loja=bz`: a TRILHA do Voltar guarda só o `pathname`, então
 * com query a pessoa que abria uma OS de Trancoso e clicava em Voltar caía em
 * Búzios. E a página da loja vira favorito no celular do instalador.
 * O menu (`CustomSidebar`) monta o mesmo caminho — mudou aqui, muda lá.
 */
export const caminhoDaLoja = (codigo: string) =>
  `/ordens-servico/loja/${codigo.toLowerCase()}`;

export interface OSItem {
  id?: number;
  descricao: string;
  quantidade: number;
  observacao?: string | null;
}

export interface Instalador {
  id: number;
  nome: string;
  foto?: string | null; // URL do avatar em /uploads, ou null
}

/**
 * Natureza do item da agenda. 'os' = ligado a uma OS (visita/instalação). Os
 * demais são a taxonomia do evento AVULSO — independente de ter OS.
 */
export type TipoEvento =
  | 'os'
  | 'entrega'
  | 'retirada'
  | 'servico'
  | 'ausencia'
  | 'reuniao'
  /** A medição ocupa a manhã de alguém e tem endereço. Estava gravada como
   *  'lembrete' em produção — o tipo que NÃO ocupa o dia —, e por isso o dia
   *  parecia livre. Entrou em 23/09, da leitura do uso real. */
  | 'medicao'
  /** Afirmação sobre o DIA, não um compromisso: "aqui não há instalação".
   *  Diferente de 'ausencia', que é uma pessoa indisponível (folga/médico). */
  | 'dia_sem_os'
  | 'lembrete';

/** Marcador "gera custo?" do evento — só a intenção, sem valor. */
export type CustoEvento = 'sim' | 'nao' | 'a_definir';

/** Prioridade do item (o "PRIORIDADE CONFIRMADO" da planilha da Suany). */
export type PrioridadeVisita = 'normal' | 'alta';
/** Motivo do "não foi feito" — só com status='nao_realizada' (texto livre em relato). */
export type MotivoVisita =
  | 'sem_ninguem'
  | 'cliente_remarcou'
  | 'faltou_material'
  | 'chuva'
  | 'outro';

export interface OSVisita {
  id?: number;
  os_id?: number; // ausente = evento avulso
  viagem_id?: number | null; // null = fora de qualquer viagem (Fatia 4)
  tipo?: TipoEvento;
  titulo?: string | null; // texto do evento avulso
  os_ref?: string | null; // referência textual a uma OS ainda não lançada
  dia_inteiro?: boolean; // false = tem hora (mata a convenção 00:00)
  data?: string | null; // 'YYYY-MM-DD'
  hora?: string | null; // 'HH:MM' (null = sem hora)
  instalador?: string | null; // legado (texto livre)
  instaladores: Instalador[];
  // 'nao_realizada' (Fatia 5a) = tentou mas não deu; visita continua viva, vai remarcar.
  status: 'agendada' | 'realizada' | 'nao_realizada' | 'cancelada';
  prioridade?: PrioridadeVisita;
  motivo?: MotivoVisita | null; // só com status='nao_realizada'
  custo?: CustoEvento; // "gera custo?" (sem valor)
  /** PAUTA é futuro: o que a equipe vai fazer nesta ida. É a única que sai no
   *  roteiro impresso, e pode ser reescrita até a véspera. */
  pauta?: string | null;
  /** DESFECHO é passado: como a ida terminou (e o detalhe do motivo quando
   *  status='nao_realizada'). Não vai pro papel. Migration 042. */
  desfecho?: string | null;
  /** Endereço PRÓPRIO do evento avulso (migration 043). Visita de OS usa o
   *  endereço da OS — aqui fica tudo nulo e a agenda/roteiro resolvem. */
  endereco?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
}

/** Visita + dados da OS, usada na tela de Agenda. */
export interface VisitaAgenda extends OSVisita {
  os_codigo?: string | null;
  os_tipo?: TipoOS | null;
  cliente_nome?: string | null;
  endereco_resumo?: string | null;
  /** Só bairro e cidade — é o que cabe no card do calendário. */
  bairro_cidade?: string | null;
  loja_codigo?: string | null;
  os_arquivada?: boolean; // OS arquivada → card borrado e travado (Fatia 5b)
  /** A pessoa ABRE esta OS? Falso = card de consulta: o vendedor vê a agenda
   *  da loja inteira, mas só clica nas OS dele (decisão de 30/10/2026). */
  os_abrivel?: boolean;
  /** O instalador vê este card? (migration 047) É o ÚNICO estado que existe:
   *  os botões de dia e de semana são atalhos que marcam/desmarcam em lote os
   *  cards do intervalo — período não é entidade, e por isso dá para marcar o
   *  dia e depois tirar um compromisso dele. */
  liberado?: boolean;
  /** A pasta a que o evento pertence (`operacao`, `pessoal`) — é o que a barra
   *  "Exibir:" liga e desliga. Opcional de propósito: resposta sem ele fica
   *  visível, porque esconder por falta de informação é a agenda sumindo
   *  sozinha. Ficha #27, Fase 2. */
  calendario?: string | null;
  /** O conteúdo foi trocado por "Ocupado"/"Ausente": é compromisso privado de
   *  outra pessoa, e quem olha não tem `agenda.pessoal.ver`. O card existe (o
   *  dia está tomado), o motivo não vem no corpo da resposta. */
  reservado?: boolean;
}

/** Uma parada do roteiro do dia: o evento da agenda + o que o instalador
 *  leva/faz (dados da OS: pedido, notas do fornecedor, "o que levar"). */
export interface RoteiroEvento {
  id: number;
  hora?: string | null;
  tipo?: TipoEvento;
  acao?: string | null; // rótulo humano (Instalação/Entrega/…)
  viagem_id?: number | null; // parada dentro de uma viagem (Fatia 4)
  viagem_nome?: string | null; // destinos da viagem encadeados
  os_id?: number | null;
  os_codigo?: string | null;
  cliente_nome?: string | null;
  titulo?: string | null; // evento avulso (sem OS)
  os_ref?: string | null; // referência textual a OS não lançada
  pedido?: string | null;
  bairro_cidade?: string | null; // o destino, que agrupa a folha
  endereco_resumo?: string | null; // rua, número, bairro e cidade
  complemento?: string | null; // apartamento/bloco, quando houver
  pauta?: string | null; // o que fazer nesta parada — o texto que vai pro papel
  notas: OSNota[];
  recursos: OSRecurso[];
}

/** Roteiro imprimível de UM dia. */
export interface RoteiroDia {
  data: string; // 'YYYY-MM-DD'
  loja_codigo?: string | null;
  eventos: RoteiroEvento[];
}

/** Roteiro imprimível de uma VIAGEM inteira: os dias como seções. */
export interface RoteiroViagem {
  viagem_id: number;
  viagem_nome?: string | null;
  dias: RoteiroDia[];
}

/** OS no painel "a agendar" da Agenda. */
export interface OSAgendar {
  id: number;
  codigo: string;
  tipo: TipoOS;
  status?: StatusOS; // p/ marcar as concluídas quando reexibidas (Fatia 5b)
  arquivada?: boolean;
  cliente_nome?: string | null;
  endereco_resumo?: string | null;
  pedido?: string | null;
  loja_codigo?: string | null;
  qtd_visitas: number;
  /** Cadeado da Agenda: marcado à mão quando se termina de agendar a OS.
   *  Não é derivado de qtd_visitas — a OS pode ter várias visitas e seguir aberta. */
  agendamento_concluido?: boolean;
}

export interface OSFoto {
  arquivo: string;
  nome?: string | null;
}

export interface OSMedida {
  abertura?: string | null;
  medida?: string | null;
  modelo?: string | null;
  fixacao?: string | null;
  obs?: string | null;
  fotos: OSFoto[];
}

export interface OSAmbiente {
  id?: number;
  descricao?: string | null;
  obs?: string | null;
  medidas: OSMedida[];
  /** Legado: fotos no nível do ambiente. Realocadas p/ a 1ª medida ao abrir. */
  fotos?: OSFoto[];
}

/** Nota fiscal (do fornecedor) da OS — linha do roteiro (Fatia 3). */
export interface OSNota {
  id?: number;
  fornecedor?: string | null;
  numero?: string | null;
  volumes?: number | null;
  conteudo?: string | null;
}

/** Item de "o que levar". `id` = recurso do catálogo; sem id = novo (backend cria).
 *  Quantidade/observação são por-OS (não do catálogo). */
export interface OSRecurso {
  id?: number;
  nome: string;
  levado?: boolean;
  quantidade?: number | null;
  observacao?: string | null;
}

export interface ExecucaoEntry {
  id: number;
  usuario_id?: number | null;
  usuario_nome?: string | null;
  usuario_foto?: string | null;
  texto?: string | null;
  fotos: OSFoto[];
  criado_em?: string | null;
}

export interface OSAnexo {
  arquivo: string;
  nome?: string | null;
  tipo?: 'imagem' | 'arquivo' | null;
}

export interface MensagemChat {
  id: number;
  usuario_id?: number | null;
  usuario_nome?: string | null;
  usuario_foto?: string | null;
  mensagem: string;
  anexos: OSAnexo[];
  criado_em?: string | null;
}

export interface EventoOS {
  id: number;
  usuario_id?: number | null;
  usuario_nome?: string | null;
  usuario_foto?: string | null;
  tipo: string; // criacao | status | edicao | cobranca | autorizacao | trava | chat | execucao
  descricao: string;
  dados?: Record<string, unknown> | null;
  criado_em?: string | null;
}

export type Cobranca = 'sim' | 'nao' | 'a_definir';

export type TipoOS = 'instalacao' | 'manutencao' | 'lavagem';
export type StatusOS =
  | 'aberta'
  | 'agendada'
  | 'em_andamento'
  | 'concluida'
  | 'cancelada';

export interface OSListItem {
  id: number;
  codigo: string;
  loja_id: number;
  loja_codigo?: string | null;
  tipo: TipoOS;
  status: StatusOS;
  cobranca: Cobranca;
  autorizacao_status?: string | null; // status derivado do módulo de Autorizações
  cliente_nome: string;
  cidade?: string | null;
  pedido_venda?: string | null;
  vendedor?: string | null;
  responsavel?: string | null;
  dt_abertura?: string | null;
}

export interface OSDetalhe extends OSListItem {
  /** O que quem pediu pode NESTA OS (âmbito, migration 126). */
  permissoes?: string[];
  cliente_telefone?: string | null;
  cliente_whatsapp?: string | null;
  cliente_email?: string | null;
  cliente_documento?: string | null;
  endereco?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  uf?: string | null;
  cep?: string | null;
  referencia?: string | null;
  observacoes?: string | null;
  cliente_obs?: string | null;
  cliente_obs_pdf?: boolean;
  obs_tiny?: string | null;
  obs_interna_tiny?: string | null;
  sem_pedido?: boolean;
  origem?: string | null;
  pv_travado?: boolean;
  responsavel_id?: number | null;
  arquiteto?: string | null;
  arquivada?: boolean; // OS arquivada (Fatia 5b) — o form já lê, faltava no tipo
  execucao_travada?: boolean;
  trava_por?: string | null;
  dt_trava?: string | null;
  justificativa_cobranca?: string | null;
  cobranca_definida_por?: string | null;
  dt_cobranca?: string | null;
  dt_conclusao?: string | null;
  dt_criacao?: string | null; // carimbo imutável: quando a OS foi cadastrada
  itens: OSItem[];
  visitas: OSVisita[];
  ambientes: OSAmbiente[];
  notas: OSNota[];
  recursos: OSRecurso[];
}

export const TIPOS: { value: TipoOS; label: string }[] = [
  { value: 'instalacao', label: 'Instalação' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'lavagem', label: 'Lavagem' },
];

export const STATUS: { value: StatusOS; label: string }[] = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'agendada', label: 'Agendada' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluida', label: 'Concluída' },
  { value: 'cancelada', label: 'Cancelada' },
];

export const STATUS_VISITA: { value: OSVisita['status']; label: string }[] = [
  { value: 'agendada', label: 'Agendada' },
  { value: 'realizada', label: 'Realizada' },
  { value: 'nao_realizada', label: 'Não realizada' },
  { value: 'cancelada', label: 'Cancelada' },
];

export const PRIORIDADES: { value: PrioridadeVisita; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
];

/** Motivos do "não foi feito" (aparecem quando status = Não realizada). */
export const MOTIVOS_NAO_REALIZADA: { value: MotivoVisita; label: string }[] = [
  { value: 'sem_ninguem', label: 'Não tinha ninguém no local' },
  { value: 'cliente_remarcou', label: 'Cliente remarcou / pediu outro dia' },
  { value: 'faltou_material', label: 'Faltou material / peça' },
  { value: 'chuva', label: 'Chuva / tempo' },
  { value: 'outro', label: 'Outro' },
];

export const rotuloMotivo = (m?: MotivoVisita | null): string =>
  m ? MOTIVOS_NAO_REALIZADA.find((x) => x.value === m)?.label ?? m : '';

export const COBRANCAS: { value: Cobranca; label: string }[] = [
  { value: 'a_definir', label: 'A definir' },
  { value: 'sim', label: 'Com cobrança' },
  { value: 'nao', label: 'Sem cobrança' },
];

export const rotuloCobranca = (c: Cobranca): string =>
  COBRANCAS.find((x) => x.value === c)?.label ?? c;

export const varianteCobranca = (
  c: Cobranca,
): 'success' | 'warning' | 'secondary' => {
  if (c === 'sim') return 'success';
  if (c === 'nao') return 'warning';
  return 'secondary';
};

export const rotuloTipo = (t: TipoOS): string =>
  TIPOS.find((x) => x.value === t)?.label ?? t;

export const rotuloStatusVisita = (s: OSVisita['status']): string =>
  STATUS_VISITA.find((x) => x.value === s)?.label ?? s;

export const varianteVisita = (
  s: OSVisita['status'],
): 'success' | 'destructive' | 'warning' | 'secondary' => {
  if (s === 'realizada') return 'success';
  if (s === 'nao_realizada') return 'warning'; // âmbar/atenção, não vermelho
  if (s === 'cancelada') return 'destructive';
  return 'secondary';
};

export const rotuloStatus = (s: StatusOS): string =>
  STATUS.find((x) => x.value === s)?.label ?? s;

type BadgeVariant = 'primary' | 'secondary' | 'success' | 'destructive' | 'warning' | 'info';

export const varianteStatus = (s: StatusOS): BadgeVariant => {
  switch (s) {
    case 'aberta':
      return 'primary';
    case 'agendada':
      return 'info';
    case 'em_andamento':
      return 'warning';
    case 'concluida':
      return 'success';
    case 'cancelada':
      return 'destructive';
    default:
      return 'secondary';
  }
};
